import { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Clock, MessageSquare } from 'lucide-react';
import MarketingShell from '../components/homepage/MarketingShell';

const API = process.env.REACT_APP_BACKEND_URL;

const INPUT_CLS = 'w-full rounded-xl border border-[#E4E8E6] bg-white px-4 py-3 text-sm text-[#18202D] outline-none transition-colors placeholder:text-[#9AA2AE] focus:border-[#27B84A] dark:border-[#1C2420] dark:bg-[#10150F] dark:text-white';

export default function ContactHub() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('failed');
      setSent(true);
      toast.success("Message sent! We'll get back to you within one business day.");
    } catch {
      toast.error('Something went wrong sending your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <MarketingShell title="Contact & Support | CountBucks">
      <section className="mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-2" data-testid="contact-hub-page">
        <div>
          <span className="inline-block rounded-full border border-[#27B84A]/40 bg-[#F2FBF4] px-4 py-1.5 text-xs font-bold tracking-widest text-[#118A35] dark:bg-[#0E1A11] dark:text-[#27B84A]">
            CONTACT & SUPPORT
          </span>
          <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Talk to a <span className="text-[#27B84A]">real human.</span>
          </h1>
          <p className="mt-4 max-w-md text-base text-[#596273] sm:text-lg dark:text-[#AEB6B1]">
            Questions about features, pricing, migration from QuickBooks, or anything else — send us a note and we'll get back to you.
          </p>
          <ul className="mt-8 space-y-5">
            <li className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#27B84A]/10"><Clock className="h-5 w-5 text-[#118A35] dark:text-[#27B84A]" /></span>
              <div>
                <p className="font-bold">Fast replies</p>
                <p className="text-sm text-[#596273] dark:text-[#8B948F]">We answer within one business day — usually much faster.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#27B84A]/10"><MessageSquare className="h-5 w-5 text-[#118A35] dark:text-[#27B84A]" /></span>
              <div>
                <p className="font-bold">Already a customer?</p>
                <p className="text-sm text-[#596273] dark:text-[#8B948F]">Log in and use the in-app Support page for priority ticket tracking.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#27B84A]/10"><Mail className="h-5 w-5 text-[#118A35] dark:text-[#27B84A]" /></span>
              <div>
                <p className="font-bold">Real CPAs on staff</p>
                <p className="text-sm text-[#596273] dark:text-[#8B948F]">Tax and accounting questions get routed to actual CPAs, not scripts.</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="rounded-3xl border border-[#E4E8E6] bg-white p-6 shadow-xl shadow-black/5 sm:p-8 dark:border-[#1C2420] dark:bg-[#10150F]">
          {sent ? (
            <div className="flex h-full flex-col items-center justify-center py-16 text-center" data-testid="contact-success">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#27B84A]/10">
                <Mail className="h-7 w-7 text-[#118A35] dark:text-[#27B84A]" />
              </span>
              <h2 className="mt-5 text-2xl font-extrabold">Message sent!</h2>
              <p className="mt-2 max-w-xs text-sm text-[#596273] dark:text-[#8B948F]">
                Thanks, {form.name.split(' ')[0]}. We'll reply to {form.email} within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#596273] dark:text-[#8B948F]">Name</label>
                  <input id="contact-name" data-testid="contact-name-input" className={INPUT_CLS} placeholder="Your name" value={form.name} onChange={set('name')} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#596273] dark:text-[#8B948F]">Email</label>
                  <input id="contact-email" type="email" data-testid="contact-email-input" className={INPUT_CLS} placeholder="you@business.com" value={form.email} onChange={set('email')} />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#596273] dark:text-[#8B948F]">Subject</label>
                <input id="contact-subject" data-testid="contact-subject-input" className={INPUT_CLS} placeholder="What's this about?" value={form.subject} onChange={set('subject')} />
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#596273] dark:text-[#8B948F]">Message</label>
                <textarea id="contact-message" rows={6} data-testid="contact-message-input" className={INPUT_CLS} placeholder="Tell us what you need…" value={form.message} onChange={set('message')} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="w-full rounded-2xl bg-[#27B84A] px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-green-600/25 transition-colors hover:bg-[#118A35] disabled:opacity-60"
              >
                {sending ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </section>
    </MarketingShell>
  );
}
